import { BreakpointValue, EntityTag, IPxConfigSection, ThemeSettings } from "@/apidtos";
import { AxiosError } from "axios";
import { UIMatch } from "@remix-run/react";
import { format, formatDistanceToNow } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { MetaArgs } from "@remix-run/node";
import _ from "lodash";
import { breakpoints, defaultInterpreter, sizeMap, weightMap } from "tailwind.constants";
import { ValueInterpreter } from "./types/ValueInterpreter";
import { Breakpoint } from "@core/types/Breakpoint";
import { ApiRoutes } from "@/apiroutes";

export const getConfig = (args: MetaArgs) => {
  const rootMatch = args.matches.find(x => x.id === "root");
  const rootLoaderData = rootMatch?.data as any;
  const config = rootLoaderData.config as Record<string, IPxConfigSection>;
  return config;
};

export const getQueryParams = (request: Request): URLSearchParams => {
  const url = new URL(request.url);
  return url.searchParams;
}

export const cleanQueryParams = (query: URLSearchParams): Record<string, any> => {
  const params = [...query.entries()]
    .filter(([_, value]) => value !== '')
    .reduce((acc, [key, value]) => {
      if (Object.hasOwn(acc, key)) {
        const current = Array.isArray(acc[key]) ? acc[key] : [acc[key]];
        acc[key] = [...current, value];
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

  // Upsert PageIndex and PageSize if they are not set.
  if (!Object.hasOwn(params, 'PageIndex')) {
    params.PageIndex = "0";
  }
  if (!Object.hasOwn(params, 'PageSize')) {
    params.PageSize = "8";
  }

  return params;
}

export const firstOrUndefined = <T,>(array: T[]): T | undefined => {
  if (array.length === 0) {
    return undefined;
  }
  return array[0];
}

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function hexToRGB(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  return { r, g, b };
}

export function getColorArray(rgb: RGB): Record<string, RGB> {
  return {
    "50": lighter(rgb, 0.9),
    "100": lighter(rgb, 0.8),
    "200": lighter(rgb, 0.6),
    "300": lighter(rgb, 0.4),
    "400": lighter(rgb, 0.2),
    "500": rgb,
    "600": darker(rgb, 0.2),
    "700": darker(rgb, 0.4),
    "800": darker(rgb, 0.6),
    "900": darker(rgb, 0.8),
    "950": darker(rgb, 0.9),
  }
}

function darker(rgb: RGB, amount: number): RGB {
  return {
    r: lerp(rgb.r, 0, amount),
    g: lerp(rgb.g, 0, amount),
    b: lerp(rgb.b, 0, amount),
  }
}

function lighter(rgb: RGB, amount: number): RGB {
  return {
    r: lerp(rgb.r, 1, amount),
    g: lerp(rgb.g, 1, amount),
    b: lerp(rgb.b, 1, amount),
  }
}

function lerp(a: number, b: number, v: number) {
  return a + (b - a) * v;
}

export function rgbString(color: RGB) {
  return `rgb(${Math.round(color.r * 255)} ${Math.round(color.g * 255)} ${Math.round(color.b * 255)})`;
}

export function pascalToKebab(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

const convertVariantStringToHex = (
  shades: Record<string, string>,
  variant: string, // "primary"
  weight: string, // "800"
): string => {
  if (!shades || !variant || !weight) {
    return variant;
  }
  const convertRgbToHex = (rgbStr: string): string => {
    // rgbStr like "rgb(00, 11, 22)"
    const parts = rgbStr.replace("rgb", "").replace("(", "").replace(")", "").split(" ").map(x => +x);
    const hex = `#${parts
      .map(n => n.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()}`;
    return hex;
  };
  // { --sections-footer-section-outer-container-border-color-sm: #123456 }
  const relevantKey = Object.keys(shades).find(x => x.includes(`${variant}-color-${weight}`));
  const rgbString = shades[relevantKey as "--success-color-500"];
  return convertRgbToHex(rgbString);
};

export const createStyleFromTheme = (theme?: ThemeSettings): Record<string, string> => {
  if (!theme) {
    return {};
  }

  const shades = {
    ...getShades(theme.PrimaryColor!, "primary"),
    ...getShades(theme.SecondaryColor!, "secondary"),
    ...getShades(theme.InfoColor!, "info"),
    ...getShades(theme.SuccessColor!, "success"),
    ...getShades(theme.WarningColor!, "warning"),
    ...getShades(theme.DangerColor!, "danger"),
    ...getShades(theme.SurfaceColor!, "surface"),
    ...getShades(theme.LinkColor!, "link"),
  };

  const interpreterMap: Record<ValueInterpreter, (val: BreakpointValue) => string> = {
    passthrough: (val) => {
      // Arbitrary: Value: "1fr 2fr" → 1fr 2fr
      return val.Value || "";
    },
    scalar: (val) => {
      // Direct: Value: "35" → 35px
      return `${val.Value || "0"}${val.Unit || ""}`;
    },
    spacing: (val) => {
      // pt-5: Value: "5" → 5 * 0.25 = 1.25rem
      const scaled = parseFloat(val.Value || "0") * 0.25; // Tailwind default multiplier
      return `${scaled}${val.Unit || "rem"}`;
    },
    "grid-count": (val) => {
      return `repeat(${val.Value || "1"}, minmax(0, 1fr))`;
    },
    color: (val) => {
      // primary & 500 → #123456
      if (val.Variant && val.Value) {
        return convertVariantStringToHex(shades, val.Variant, val.Value || "");
      }
      return val.Value || "";
    },
    "font-weight": (val) => {
      // "bold" → 700
      return weightMap[val.Value || "normal"] || val.Value as string;
    },
    size: (val) => {
      // sm → 0.875rem
      return sizeMap[val.Value as "xs"];
    },
    ordinal: (val) => {
      // order-1 → 1
      return val.Value as string;
    }
  };

  function createCssVariablesFromTheme(
    node: any,
    path: string[] = []
  ): Record<string, string> {
    const cssVars: Record<string, string> = {};

    if (typeof node !== "object" || node === null) {
      return cssVars;
    }

    for (const key of Object.keys(node)) {
      const value = node[key];
      const nextPath = [...path, pascalToKebab(key)];

      const isNicheParentValue = value
        && typeof value === "object"
        && "Value" in value
        && typeof value.Value === "string";
      const isBreakpointValues = value
        && typeof value === "object"
        && Object.keys(value).some(x => breakpoints.includes(x as Breakpoint));
      if (isNicheParentValue) {
        // for niche cases like the Variant of the MiniMenu button which
        // has too much styling to do it manually and never changes based on screen width
        cssVars[`--${nextPath.join("-")}`] = value.Value;
      } else if (isBreakpointValues) {
        // BreakpointValues
        const cssVarName = `--${nextPath.slice(0, nextPath.length - 1).join("-")}`;
        Object.entries(value as Record<string, BreakpointValue & { ValueInterpreter?: ValueInterpreter }>)
          .forEach(([ brkPt, brkPtVal]) => {
            let innerValue = "";
            // Resolve interpreter: breakpoint > parent > default
            const resolvedInterpreter = brkPtVal.ValueInterpreter || node.ValueInterpreter || defaultInterpreter;
            const computeFn = interpreterMap[resolvedInterpreter as ValueInterpreter] || interpreterMap[defaultInterpreter];
            innerValue = computeFn(brkPtVal);
            cssVars[`${cssVarName}-${brkPt as Breakpoint}`] = innerValue;
          });
      } else if (typeof value === "object" && value !== null && Object.keys(value).length) {
        Object.assign(cssVars, createCssVariablesFromTheme(value, nextPath));
      }
    }
    return cssVars;
  }

  return {
    ...shades,
    // Base color aliases (point to -500 variants)
    "--primary-color": theme.PrimaryColor!,
    "--secondary-color": theme.SecondaryColor!,
    "--info-color": theme.InfoColor!,
    "--success-color": theme.SuccessColor!,
    "--warning-color": theme.WarningColor!,
    "--danger-color": theme.DangerColor!,
    "--surface-color": theme.SurfaceColor!,
    "--link-color": theme.LinkColor!,
    // Legacy name; same stack as Tailwind’s --font-family-sans below.
    "--sans-serif-font-family": theme.SansSerifFontFamily!,
    // Drives Tailwind fontFamily.sans / .font-sans — set on <body> so it wins over :root defaults.
    "--font-family-sans": theme.SansSerifFontFamily!,
    "--font-family-serif": theme.SerifFontFamily!,
    "--border-width": theme.BorderSize!,
    "--container-radius": theme.ContainerRadius!,
    "--base-radius": theme.BaseRadius!,
    "--form-radius": theme.FormRadius!,
    "--button-radius": theme.ButtonRadius!,
    "--vertical-outlet-padding": theme.VerticalOutletPadding!,
    "--horizontal-outlet-padding": theme.HorizontalOutletPadding!,
    ...createCssVariablesFromTheme(theme),
  };
};

/**
 * Reduces an object of colors to a record of CSS variable strings.
 *
 * @param colors - An object where keys are color names and values are color values.
 * @param title - A string to prefix the CSS variable names.
 * @returns A record of CSS variable strings.
 */
const getShades = (color: string, title: string) => {
  const rgb = hexToRGB(color);
  const colors = getColorArray(rgb);
  return Object.keys(colors).reduce(
    (prev, cur) => ({ ...prev, [`--${title}-color-${cur}`]: rgbString(colors[cur]) }),
    {} as Record<string, string>
  );
}


/**
 * Converts a DTO object to a string key fragment.
 *
 * @param dto - The DTO object to convert.
 * @returns A string key fragment representing the DTO.
 */
export const getDtoAsPromiserKeyFragment = (dto: object): string => {
  if (!dto || typeof dto !== "object") {
    return "";
  }
  return Object.keys(dto)
    .sort()
    .reduce((str, key) => {
      if (Object.hasOwn(dto, key)) {
        return `${str}${key}:${JSON.stringify(dto[key as keyof object])}`;
      }
      return str;
    }, "");
};

/**
 * Parses a prefixed value string and returns the actual value.
 *
 * @param value - The prefixed value string to parse.
 * @returns The parsed value, which can be a boolean, number, or the original string.
 */
export const parsePrefixedValue = (value: string) => {
  try {
    const [prefix, actualValue] = value.split(':');

    switch (prefix) {
      case "boolean": {
        return actualValue.toLowerCase() === "true";
      }
      case "number": {
        const parsedValue = parseInt(actualValue, 10);
        return Number.isNaN(parsedValue) ? null : parsedValue;
      }
      default: {
        return value;
      }
    }
  } catch (error) {
    console.warn("Error parsing value:", error);
    return value;
  }
}

export const formatName = (str?: string) => str?.replace(/([^[\p{L}\d]+|(?<=[\p{Ll}\d])(?=\p{Lu})|(?<=\p{Lu})(?=\p{Lu}[\p{Ll}\d])|(?<=[\p{L}\d])(?=\p{Lu}[\p{Ll}\d]))/gu, " ");

/**
 * Parses the Cookie header and returns an object with key-value pairs.
 *
 * @param request - The request object containing the Cookie header.
 * @returns An object representing the parsed cookies.
 */
export const parseCookies = (request: Request) => {
  const cookieHeader = request.headers.get('Cookie');
  if (!cookieHeader) {
    return {};
  }

  return cookieHeader.split(';').reduce((cookies, cookie) => {
    const [name, value] = cookie.split('=').map(part => part.trim());
    cookies[name] = decodeURIComponent(value);
    return cookies;
  }, {} as Record<string, string>);
};

export const hasTag = (obj: { Tags?: EntityTag[] } | undefined | null, key: string) => {
  if (!obj?.Tags?.length) {
    return false;
  }
  return obj.Tags.some(x => x.Key === key);
}

export const hasTagValue = (obj: { Tags?: EntityTag[] } | undefined | null, key: string, value: string) => {
  if (!obj?.Tags?.length) {
    return false;
  }
  return obj.Tags.some(x => x.Key === key && x.Value === value);
};

export const tagValue = (obj: { Tags?: EntityTag[] } | undefined | null, key: string) => {
  if (!obj?.Tags?.length) {
    return "";
  }
  const tag = obj.Tags.find(x => x.Key === key);
  return tag?.Value ?? "";
}

export const combineCookies = (cookies: string[] | undefined): [string, string][] => {
  return cookies?.map(c => ["Set-Cookie", c]) ?? [];
};

export const axiosErrorRouteResponse = (error: AxiosError, message = "An error occurred while executing the action"): Response => {
  if (error.isAxiosError && error.response && error.response.data) {
    // @ts-expect-error api errors contain "exceptionDetails" only in development mode
    if (error.response.data.exceptionDetails) {
      // @ts-expect-error api errors contain "exceptionDetails" only in development mode
      const exceptionDetails = error.response.data.exceptionDetails;
      const errorMessage = exceptionDetails.map((detail: any) => detail.Message).join(", ");
      return new Response(`${message}: ${errorMessage}`, { status: error.response.status || 500 });
    }
    // return new Response(`${message}: ${JSON.stringify(error.response.data)}`, { status: error.response.status || 500 });
  }
  return new Response(message, { status: error.response?.status || 500 });
}

export interface IHandleWithBreadcrumbs {
  breadcrumb:
    | React.ReactNode
    | { key: string; fallback?: string; id?: string | number }
    | ((match: UIMatch) => React.ReactNode | { key: string; fallback?: string; param?: string | number });
}

export const matchHasBreadcrumb = (match: UIMatch<any, any>): match is UIMatch<unknown, IHandleWithBreadcrumbs> => {
  const { handle } = match;
  if (Array.isArray(handle)) {
    return handle.every(item => "breadcrumb" in item);
  }
  return Boolean(handle) && "breadcrumb" in handle;
};


export const renderBreadcrumb = (
  match: UIMatch<unknown, IHandleWithBreadcrumbs>,
  t: (key: string, fallback?: string) => string
) => {
  const breadcrumb = match.handle.breadcrumb;
  if (typeof match.handle.breadcrumb === "function") {
    const result = match.handle.breadcrumb(match);
    // If the result is an object with key/fallback/id, handle it here too
    if (
      typeof result === "object" &&
      result !== null &&
      "key" in result
    ) {
      const key = (result as { key?: string }).key ?? "";
      const fallback = (result as { fallback?: string }).fallback;
      const param = (result as { param?: string | number }).param;
      const label = t(key, fallback);
      return param !== undefined ? `${label} ${param}` : label;
    }
    return result;
  }
  // If it's an object with a key, use key and fallback for translation
  if (
    typeof breadcrumb === "object" &&
    breadcrumb !== null &&
    "key" in breadcrumb
  ) {
    const key = (breadcrumb as { key?: string }).key ?? "";
    const fallback = (breadcrumb as { fallback?: string }).fallback;
    const id = (breadcrumb as { id?: string | number }).id;
    const label = t(key, fallback);
    return id !== undefined ? `${label} #${id}` : label;
  }
  // If it's a string, treat it as the breadcrumb name (no translation)
  if (typeof breadcrumb === "string") {
    return breadcrumb;
  }
  // Otherwise, return as-is (could be a React element)
  return breadcrumb;
};

export const getValues = (e: object) => {
  return Object.entries(e)
    .map(([ key, value ]) => ({ key, value }))
    .filter(kvp => typeof kvp.value !== "number");
};

/**
 * Utility type to extract the resolved value of the json() method from a loader function.
 * @example
 * import type { loader } from "@/routes/_site.dashboard.mylistings.create.route";
 * const { products } = useOutletContext<OutletContextResolvedType<typeof loader>>();
 */
export type OutletContextResolvedType<T extends (...args: any) => any> = T extends (...args: any) => Promise<infer R> ? R extends { json: () => Promise<infer U> } ? U : never : never;

export const formatTimeDistanceToNow = (zonedDate: Date) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const utcDate = fromZonedTime(zonedDate, timeZone)
  return formatDistanceToNow(utcDate)
}

export const toTitleCase = (str: string) => {
  return _.startCase(_.camelCase(str));
};

export const getUnitFromString = (str: string): string => {
  return str.replace(/^[\d.]+/, "").trim();
};

export const fixDateTimezoneOffset = (date: string, dateformat?: string) => {
    // You have to subtract the timezone offset to get the correct date in the input field
    const dt = new Date(date);
    const dtDateOnly = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
    if (dateformat)
    {
      return format(dtDateOnly, dateformat);
    }
    return format(dtDateOnly, 'yyyy-MM-dd');
}

type FunctionKeys<T> = { [K in keyof T]-?: T[K] extends (...args: any) => any ? K : never }[keyof T];
type ApiCallableKey = FunctionKeys<ApiRoutes>;

export function toApiRoute(input: string, api: ApiRoutes): ApiCallableKey {
  const r = (input.startsWith("/") ? input : "/" + input.replace(/\./g, "/").toLowerCase()) as string;
  if (r in api && typeof (api as any)[r] === "function") return r as ApiCallableKey;
  throw new Error(`Unknown route: ${r}`);
}

export function minutes(n: number): number {
  return n * 60 * 1000;
}

export function pascalCaseToHumanReadable(input: string): string {
    if (!input) {
        return "";
    }
    const spacedString = input.replace(/([A-Z])(?=[a-z])/g, ' $1')
                              .replace(/([a-z])([A-Z])/g, '$1 $2');
    const humanReadable = spacedString.charAt(0).toUpperCase() + spacedString.slice(1);
    return humanReadable.trim();
}